// components
import { MlServiceLocations } from './ml-service-locations';

// constants
import { stateAbbreviationMap } from '@/components/organisms/or-service-areas/constants';

// hooks
import { useLocation } from '@/context/locationContext';

// types
import { TMlServiceLocationsCS } from './ml-service-locations.types';
import { IDesignStudio } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';

/**
 * CMS wrapper for MlServiceLocations.
 * Pulls data from Contentstack block, filters out digital-only studios,
 * sorts them by state, converts state abbreviations to full names,
 * and passes the grouped data to the presentational component.
 *
 * Should only be used on the Contact Us page.
 */

export const MlServiceLocationsCS = ({ block }: TMlServiceLocationsCS) => {
  const { department_contacts, show_service_locations } = block;
  const { allStudios } = useLocation();

  if (!show_service_locations) return null;

  // Filters out digital-only studios
  function isPhysicalStudio(studio: IDesignStudio): boolean {
    return !studio.IsDigital;
  }

  // Sorts studios by state abbreviation
  function sortByState(a: IDesignStudio, b: IDesignStudio): number {
    return a.State.localeCompare(b.State);
  }

  // Gets the full state name, or falls back to abbreviation
  function getFullStateName(abbreviation: string): string {
    return stateAbbreviationMap[abbreviation] || abbreviation;
  }

  // Groups studios by full state name
  function groupByState(
    acc: Record<string, IDesignStudio[]>,
    studio: IDesignStudio,
  ): Record<string, IDesignStudio[]> {
    const fullStateName = getFullStateName(studio.State);

    if (!acc[fullStateName]) {
      acc[fullStateName] = [];
    }

    acc[fullStateName].push(studio);
    return acc;
  }

  const groupedStudios = allStudios
    .filter(isPhysicalStudio)
    .sort(sortByState)
    .reduce(groupByState, {});

  return (
    <MlServiceLocations
      otherDepartments={department_contacts}
      studios={groupedStudios}
    />
  );
};
